import React, {
  Fragment,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import classNames from "classnames";

import { Visible } from "./visible";
import { Content } from "./content";
import { TalkWithUsPromptWithContext } from "./template_offering_checkout_common";
import { Container, Pressable, Row } from "../components";
import {
  buildPathWithParams,
  useUTMQueryString,
  useMediaQuery,
} from "../hooks";
import { LocalePicker } from "./locale_picker";
import { Locale, useI18n } from "../lib/i18n/i18n_provider";
import { Spacer } from "./spacer";
import { useOfferingsMenuQuery } from "./api";
import { useHistory } from "react-router-dom";
import { Text } from "./text";
import { OfferingMenu } from "../data/partner_offering";
import { Picker } from "./picker";
import { Hidden } from "./hidden";
import { usePrevious } from "../hooks/use_previous";
import { useAnalytics } from "../lib/analytics/analytics";
import { LocaleOption } from "./locale_data";

interface OfferingHeaderProps {
  logo: string;
  homepageURL: string;
  offeringID: string;
  showLanguageSelector: boolean;
  localeOptions?: LocaleOption[];
  showTalkWithUsPrompt?: boolean;
  hubspotForm?: React.ReactNode;
  talkWithUsExternalUrl?: string;
  partnerID: string;
  onLocaleSwitch?: () => void;
}

export function OfferingHeader(props: OfferingHeaderProps) {
  const {
    logo,
    homepageURL,
    offeringID,
    hubspotForm,
    talkWithUsExternalUrl,
    localeOptions,
    showLanguageSelector,
    showTalkWithUsPrompt = true,
    partnerID,
    onLocaleSwitch,
  } = props;
  const { locale, onChangeLocale } = useI18n();
  const offeringMenu = useOfferingsMenuQuery(partnerID);
  const preLocale = usePrevious(locale);
  const [renderedOfferingMenu, setRenderedOfferingMenu] =
    useState<Boolean>(true);

  /* Re-rendered OfferingMenuHeader when user switch locale */
  useEffect(() => {
    if (preLocale.baseLanguageTag !== locale.baseLanguageTag) {
      setRenderedOfferingMenu(false);
      setTimeout(() => {
        setRenderedOfferingMenu(true);
      });
    }
  }, [locale, preLocale]);

  const utmQuery = useUTMQueryString();
  const homepageURLWithUTMParams = `${homepageURL}${utmQuery}`;

  const onLocalePickerSelect = useCallback(
    (locale: Locale) => {
      onChangeLocale(locale);
      if (onLocaleSwitch) onLocaleSwitch();
    },
    [onChangeLocale, onLocaleSwitch]
  );

  const analytics = useAnalytics();

  const onLogoClick = useCallback(() => {
    analytics.event(
      "Logo Click",
      {
        Source: "WL",
      },
      { onlyProviders: ["mixpanel", "customerio"] }
    );
  }, [analytics]);

  return (
    <Content>
      <Container paddingHorizontal={16} height={80}>
        <Spacer size={16} />
        <Visible md>
          <div className="offering-header-desktop-wrapper">
            <Row
              flex={1}
              alignItems="center"
              justifyContent={
                offeringMenu && renderedOfferingMenu
                  ? "center"
                  : "space-between"
              }
            >
              <a
                className="offering-header-logo-link"
                href={homepageURLWithUTMParams}
                onMouseDown={onLogoClick}
              >
                <img className="offering-header-logo" alt="logo" src={logo} />
              </a>
              {offeringMenu && renderedOfferingMenu && (
                <OfferingMenuHeader
                  offeringID={offeringID}
                  offeringMenu={offeringMenu}
                />
              )}
              <Row alignItems="center">
                {showLanguageSelector && localeOptions && (
                  <Fragment>
                    <Container
                      paddingLeft={locale.direction === "ltr" ? 48 : 0}
                      minWidth={130}
                      paddingRight={locale.direction === "rtl" ? 48 : 0}
                    >
                      <Visible md>
                        <LocalePicker
                          localeOptions={localeOptions}
                          locale={locale}
                          onChangeLocale={onLocalePickerSelect}
                        />
                      </Visible>
                    </Container>
                    <Spacer
                      size={16}
                      direction="horizontal"
                      className="size_in_language"
                    />
                  </Fragment>
                )}
                {showTalkWithUsPrompt && (
                  <Container
                    paddingLeft={
                      locale.direction === "ltr" && !showLanguageSelector
                        ? 48
                        : 0
                    }
                    paddingRight={
                      locale.direction === "rtl" && !showLanguageSelector
                        ? 48
                        : 0
                    }
                    minWidth={200}
                    center
                  >
                    <TalkWithUsPromptWithContext
                      partnerID={partnerID}
                      hubspotForm={hubspotForm}
                      messageableID={offeringID}
                      talkWithUsExternalUrl={talkWithUsExternalUrl}
                    />
                  </Container>
                )}
              </Row>
            </Row>
          </div>
        </Visible>
        {/* In mobile-view, offering menus are hidden in header, but displayed in footer ref to OfferingFooterMobile */}
        <Hidden md>
          <Row flex={1}>
            <a
              className="offering-header-logo-link"
              href={homepageURLWithUTMParams}
              onMouseDown={onLogoClick}
            >
              <img className="offering-header-logo" alt="logo" src={logo} />
            </a>
          </Row>
        </Hidden>
      </Container>
    </Content>
  );
}

interface OfferingMenuHeaderProps {
  offeringMenu: OfferingMenu;
  offeringID: string;
}

function OfferingMenuHeader(props: OfferingMenuHeaderProps) {
  const { offeringMenu, offeringID } = props;
  const mq = useMediaQuery();
  const divRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [overflow, setOverflow] = useState<boolean | null>(null);
  const [wrapperWidth, setWrapperWidth] = useState<number | null>(null);
  const history = useHistory();
  const analytics = useAnalytics();

  useEffect(() => {
    if (wrapperRef.current !== null && wrapperRef.current.clientWidth !== 0) {
      setWrapperWidth(wrapperRef.current.clientWidth);
    }
  }, [wrapperRef, mq]);

  useEffect(() => {
    if (divRef.current !== null && wrapperWidth !== null) {
      const divElement = divRef.current;
      const elementsWidth = divElement.clientWidth;

      if (elementsWidth > wrapperWidth) {
        setOverflow(true);
      } else {
        setOverflow(false);
      }
    }
  }, [divRef, mq, wrapperWidth]);

  const onOfferingMenuClick = useCallback(
    (menu: string) => {
      analytics.event("Menu Clicked", {
        label: menu,
        category: "Behavior",
        Source: "WL",
      });

      // We don't want to keep previous filter when going to a new offering page of a different type
      const pathWithParams = buildPathWithParams(`/offerings/${menu}`, {
        reset: true,
      });
      history.push(pathWithParams);
    },
    [analytics, history]
  );

  return (
    /* !!wrapperWidth: using double negation to have a boolean value instead of the value number (0, 1) */
    <div style={{ flex: 1 }} ref={wrapperRef}>
      {!!wrapperWidth &&
        offeringMenu?.showOfferingMenu &&
        (overflow ? (
          <div className="offering-menu-overflow-dropdown">
            <Container>
              <OfferingMenuPicker
                offeringMenu={offeringMenu}
                offeringID={offeringID}
                onOfferingMenuClick={onOfferingMenuClick}
              />
            </Container>
          </div>
        ) : (
          <div
            className={classNames(
              "offering-menu-size-detector",
              overflow === false && "offering-menu-one-line"
            )}
            ref={divRef}
          >
            <OfferingMenuOneLine
              offeringMenu={offeringMenu}
              onOfferingMenuClick={onOfferingMenuClick}
            />
          </div>
        ))}
    </div>
  );
}

interface OfferingMenuPickerProps {
  offeringMenu: OfferingMenu;
  offeringID: string;
  onOfferingMenuClick: (menu: string) => void;
}

export function OfferingMenuPicker(props: OfferingMenuPickerProps) {
  const { offeringMenu, offeringID, onOfferingMenuClick } = props;
  const options = offeringMenu.offerings.map((o) => ({
    label: o.menuDisplayName,
    value: o.slug,
  }));
  const { fbt } = useI18n();

  return (
    <Picker
      options={options}
      title={fbt("Additional Products")}
      value={offeringID}
      onChange={(slug) => onOfferingMenuClick(slug)}
    />
  );
}

interface OfferingMenuOneLineProps {
  offeringMenu: OfferingMenu;
  onOfferingMenuClick: (menu: string) => void;
}

export function OfferingMenuOneLine(props: OfferingMenuOneLineProps) {
  const { offeringMenu, onOfferingMenuClick } = props;
  const { locale } = useI18n();

  return (
    <Fragment>
      {offeringMenu.offerings.map((menu, index) => (
        <Container
          paddingLeft={
            (locale.direction === "ltr" && index === 0) ||
            (locale.direction === "rtl" &&
              index === offeringMenu.offerings.length - 1)
              ? 0
              : 18
          }
          key={menu.id}
        >
          <Pressable onPress={() => onOfferingMenuClick(menu.slug)}>
            <Text nowrap={true}>{menu.menuDisplayName}</Text>
          </Pressable>
        </Container>
      ))}
    </Fragment>
  );
}
